import React from 'react';

import {
  TextField,
  DateField,
  Datagrid,
  ReferenceField,
  ArrayField,
  usePermissions,
  useRecordContext,
  RaRecord,
  ChipField,
  Edit,
  TabbedShowLayout,
} from 'react-admin';

import {
  Box,
  Card,
  Grid,
  Typography,
  CardHeader,
  CardContent,
  Divider,
} from '@mui/material';

import ScienceIcon from '@mui/icons-material/Science';

import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import StatusField from '../../customFields/StatusField';
import ColoredScoreField from '../../customFields/ColoredScoreField';

import hasAccess from '../../utilities/hasAccess';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';

import PingRequestList from './components/PingRequestList';
import UpdatePingStatus from './components/UpdatePingStatus';
import PingBreadcrumb from './PingBreadcrumb';

import NoResults from '../layout/NoResults';
import CommentList from '../comment/CommentList';
import actions, { Actions } from '../../constants/actions';
import useValidateId from '../../hooks/useValidateId';
import resourceTypes from '../../constants/resourceTypes';
import EditReferenceManyField from '../../customFields/EditReferenceManyField';
import RuleReferenceName from '../rules/components/RuleReferenceName';
import RiskClassField from '../../customFields/RiskClassField';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';
import EditToolbar from '../layout/EditToolbar';
import AuditResourceList from '../audit/AuditResourceList';
import PingDetailsCard from './components/PingDetailsCard';

const PingRule = () => {
  const ping = useRecordContext();

  if (!ping?.rule) return null;

  return (
    <Grid size={12}>
      <Box display="flex" flexDirection="column">
        <Typography variant="smallTitle">Rule</Typography>
        <ReferenceField source="rule" reference="rules" link={(record: RaRecord) => `/rules/${record.id}/summary`}>
          <RuleReferenceName />
        </ReferenceField>
      </Box>
    </Grid>
  );
};

const IsTestPing = () => {
  const record = useRecordContext();

  if (!record?.isTestPing) return null;

  return (
    <ChipField icon={<ScienceIcon />} record={{ value: 'Test Ping' }} color="primary" source="value" />
  );
};

const PingShow = () => {
  const { permissions } = usePermissions<Actions[]>();
  const validatedId = useValidateId();
  if (!validatedId.isValid) return null;

  return (
    <Edit
      mutationMode="pessimistic"
      redirect={false}
      actions={(
        <EditToolbar>
          {hasAccess(permissions, actions.PING_UPDATE_STATUS) && <UpdatePingStatus />}
          <IsTestPing />
        </EditToolbar>
      )}
    >
      <PingBreadcrumb />
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Information">
          <Grid container spacing={6}>
            <Grid
              size={{
                xs: 12,
                lg: 6,
              }}
            >
              <Card variant="outlined">
                <CardHeader title="Ping details" />
                <Divider />
                <CardContent>
                  <Grid container spacing={6}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">ID</Typography>
                        <TextField source="id" label="ID" />
                      </Box>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Status</Typography>
                        <StatusField source="status" />
                      </Box>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Score</Typography>
                        <ColoredScoreField source="score" />
                      </Box>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography
                          variant="smallTitle"
                        >
                          Score expiry date
                        </Typography>
                        <DateField source="riskExpiryDate" showTime emptyText="-" />
                      </Box>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Created at</Typography>
                        <DateField source="createdAt" showTime />
                      </Box>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Expiry date</Typography>
                        <DateField source="expiryDate" showTime emptyText="-" />
                      </Box>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Ping deadline</Typography>
                        <DateField source="pingDeadline" showTime emptyText="-" />
                      </Box>
                    </Grid>
                    {hasAccess(permissions, actions.TAG_LIST) && (
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <EditReferenceManyField
                          withColor
                          showEdit={hasAccess(permissions, actions.PING_UPDATE)}
                          source="tags"
                          reference="tags"
                          label="Tags"
                        />
                      </Grid>
                    )}
                    <Grid size={12}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Description</Typography>
                        <TextField source="description" emptyText="-" />
                      </Box>
                    </Grid>
                    <PingRule />
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid
              size={{
                xs: 12,
                lg: 6,
              }}
            >
              <CommentList action={actions.PING_ADD_COMMENT} source="comments" deleteAction={actions.PING_DELETE_COMMENT} />
            </Grid>
            <Grid size={12}>
              <Card variant="outlined">
                <CardHeader title="Entities" />
                <Divider />
                <ArrayField source="entities">
                  <Datagrid
                    sx={boldDataGridStyle}
                    empty={<NoResults />}
                    bulkActionButtons={false}
                  >
                    <TextField sortable={false} source="id" label="ID" />
                    <ReferenceEntityField source="" label="Display name">
                      <EntityDisplayNameField />
                    </ReferenceEntityField>
                    <TextField sortable={false} source="type" />
                    <ReferenceEntityField source="" linkType={false} label="Risk score">
                      <ColoredScoreField source="riskScore" />
                    </ReferenceEntityField>
                    <ReferenceEntityField source="" linkType={false} label="Risk class">
                      <RiskClassField source="riskScore" />
                    </ReferenceEntityField>
                  </Datagrid>
                </ArrayField>
              </Card>
            </Grid>
            <PingDetailsCard />
            <Grid size={12}>
              <Card variant="outlined">
                <CardHeader title="Request" />
                <Divider />
                <PingRequestList />
              </Card>
            </Grid>
          </Grid>
        </TabbedShowLayout.Tab>
        {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
          <TabbedShowLayout.Tab label="Audit trail">
            <AuditResourceList resourceType={resourceTypes.PING} />
          </TabbedShowLayout.Tab>
        )}
      </TabbedShowLayout>
    </Edit>
  );
};

export default PingShow;
